import PropTypes from "prop-types";
import { useEffect, useRef, useState, useCallback } from "react";
import * as d3 from "d3";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import Loader from "../Loader";
import NoData from "../Nodata";
import { calculateOnlineOfflinePercent, tzTickFormat, formatTooltipDateTime } from "../../utils/utility";
import { escapeHtml } from "../../utils/apiValidation";
import { CHART_CONSTANTS } from "../../utils/constants";
import OnlineOfflineLegend from "./onlineOfflineLegend";
import ChartContextMenu from "../LineBarComposedChart_new/components/ChartContextMenu";
import useStepChartExport from "./hooks/useStepChartExport";
import "./StepChart.scss";
import {
  MARGIN,
  COLOR_LINE,
  COLOR_FILL_TOP,
  COLOR_FILL_BOTTOM,
  nextChartId,
  prepareChartData,
  clampZoomDomain,
} from "./d3utility";

dayjs.extend(utc);
dayjs.extend(tz);

// ── Component ─────────────────────────────────────────────────────────────────
function StepGraph({ data, loading, dateRange, onZoomChange, zoomExtremes, siteTimezone }) {
  const [percent, setPercent] = useState({ online: 0, offline: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (menuOpen) setMenuOpen(false);
    };
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, [menuOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const handler = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  // DOM refs
  const wrapperRef = useRef(null); // outermost wrapper (used for fullscreen & tooltip positioning)
  const svgContainerRef = useRef(null); // the empty div where D3 renders the SVG

  // Track container size so D3 re-renders when the window is resized/maximized
  useEffect(() => {
    const container = svgContainerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ width, height });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Data / D3 state refs
  const chartDataRef = useRef(null); // prepared chart data (read by export handlers)
  const resetZoomRef = useRef(null); // D3 reset-zoom function exposed to React
  const chartIdRef = useRef(nextChartId());
  const xScaleRef = useRef(null); // Store xScale for external zoom updates
  const updateChartRef = useRef(null); // Store updateChart function for external zoom
  const currentDomainRef = useRef(null); // Persists active zoom domain across resize re-renders

  // ── D3 chart rendering ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!data || data.length === 0 || !svgContainerRef.current) return;

    // 1. Prepare chart data
    const chartData = prepareChartData(data, dateRange);
    chartDataRef.current = chartData;

    // 2. Calculate initial online/offline percent
    const { percentOnline, percentOffline } = calculateOnlineOfflinePercent([...chartData], dateRange);
    setPercent({ online: percentOnline, offline: percentOffline });

    // 3. Dimensions
    const container = svgContainerRef.current;
    const totalWidth = container.clientWidth;
    const totalHeight = container.clientHeight;
    const width = totalWidth - MARGIN.left - MARGIN.right;
    const height = totalHeight - MARGIN.top - MARGIN.bottom;

    if (width <= 0 || height <= 0) return;

    // 4. Clear any previous render
    d3.select(container).selectAll("*").remove();

    const chartId = chartIdRef.current;

    // 5. Root SVG
    const svg = d3.select(container).append("svg").attr("width", totalWidth).attr("height", totalHeight);

    const defs = svg.append("defs");

    // Clip path – keeps the step-area inside the plot area during zoom/pan
    defs.append("clipPath").attr("id", `${chartId}-clip`).append("rect").attr("width", width).attr("height", height);

    // Vertical linear gradient
    const grad = defs
      .append("linearGradient")
      .attr("id", `${chartId}-gradient`)
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");
    grad.append("stop").attr("offset", "0%").attr("stop-color", COLOR_FILL_TOP);
    grad.append("stop").attr("offset", "100%").attr("stop-color", COLOR_FILL_BOTTOM);

    const g = svg.append("g").attr("transform", `translate(${MARGIN.left},${MARGIN.top})`);

    // 6. Scales
    const xExtent = d3.extent(chartData, d => d.x);
    // Keep track of the original epoch domain for clamping during pan and for reset-zoom
    const originalDomainMs = [xExtent[0], xExtent[1]];

    const xScale = d3
      .scaleTime()
      .domain([new Date(xExtent[0]), new Date(xExtent[1])])
      .range([0, width]);

    const yScale = d3.scaleLinear().domain([0, 1]).range([height, 0]);

    // 7. Axes
    // X axis (datetime) — timezone-aware tick formatter
    const xAxisGroup = g
      .append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale).tickFormat(date => tzTickFormat(date, siteTimezone)));

    // Y axis – only tick positions 0 and 1 (mirrors tickPositions:[0,1])
    g.append("g")
      .attr("class", "y-axis")
      .call(d3.axisLeft(yScale).tickValues([0, 1]).tickFormat(d3.format("d")));

    // 8. Step-area series
    const areaGen = d3
      .area()
      .x(d => xScale(new Date(d.x)))
      .y0(yScale(0))
      .y1(d => yScale(d.y))
      .curve(d3.curveStepAfter);

    const lineGen = d3
      .line()
      .x(d => xScale(new Date(d.x)))
      .y(d => yScale(d.y))
      .curve(d3.curveStepAfter);

    const seriesGroup = g.append("g").attr("clip-path", `url(#${chartId}-clip)`);

    const areaPath = seriesGroup
      .append("path")
      .datum(chartData)
      .attr("fill", `url(#${chartId}-gradient)`)
      .attr("d", areaGen);

    const linePath = seriesGroup
      .append("path")
      .datum(chartData)
      .attr("fill", "none")
      .attr("stroke", COLOR_LINE)
      .attr("stroke-width", 1)
      .attr("d", lineGen);

    // 9. Tooltip (HTML div, outside SVG)
    const tooltipDiv = d3.select(wrapperRef.current).append("div").attr("class", "d3-tooltip");

    // Bisector to find the nearest data point (left of the cursor)
    const bisect = d3.bisector(d => d.x).left;

    const positionTooltip = mouseX => {
      const tooltipNode = tooltipDiv.node();
      const tooltipWidth = tooltipNode.offsetWidth || 150;
      const tooltipHeight = tooltipNode.offsetHeight || 40;

      // Positions relative to the outer wrapper (same reference as position:absolute)
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const svgRect = svgContainerRef.current.getBoundingClientRect();

      const relLeft = svgRect.left - wrapperRect.left;
      const relTop = svgRect.top - wrapperRect.top;

      // Horizontal: centered on mouseX, clamped inside plot area
      let ttX = relLeft + MARGIN.left + mouseX - tooltipWidth / 2;
      const minX = relLeft + MARGIN.left;
      const maxX = relLeft + MARGIN.left + width - tooltipWidth;
      ttX = Math.max(minX, Math.min(maxX, ttX));

      // Vertical: above the top of the plot area (mirrors positioner y:-20)
      const ttY = relTop + MARGIN.top - CHART_CONSTANTS.TOOLTIP_OFFSET_Y - tooltipHeight;

      tooltipDiv.style("left", `${ttX}px`).style("top", `${Math.max(0, ttY)}px`);
    };

    const showTooltip = event => {
      const [mouseX] = d3.pointer(event);
      const xVal = xScale.invert(mouseX).getTime();

      // For curveStepAfter the active segment [idx-1, idx) takes the y of idx-1
      const idx = bisect(chartData, xVal, 1);
      const activePoint = chartData[idx - 1] ?? chartData[0];
      if (!activePoint) return;

      const statusColor = activePoint.y === 1 ? "green" : "red";
      const statusText = activePoint.y === 1 ? "ONLINE" : "OFFLINE";
      const reasonHtml = activePoint.y === 0 ? `<br/> Reason: ${escapeHtml(activePoint.rsn || "N/A")}` : "";

      tooltipDiv
        .html(
          `<b>${formatTooltipDateTime(activePoint.x, siteTimezone)}</b><br/>` +
            `Status: <span style="color:${statusColor};font-weight:700;">${statusText}</span>` +
            reasonHtml
        )
        .style("display", "block");

      positionTooltip(mouseX);
    };

    // 10. Chart-update helper (called by zoom & pan)
    const updateChart = newDomainMs => {
      xScale.domain([new Date(newDomainMs[0]), new Date(newDomainMs[1])]);
      xAxisGroup.call(d3.axisBottom(xScale).tickFormat(date => tzTickFormat(date, siteTimezone)));
      areaPath.attr("d", areaGen);
      linePath.attr("d", lineGen);

      // Persist the active zoom domain so it can be restored after a resize re-render
      currentDomainRef.current = newDomainMs;

      // Recalculate online/offline percent for the new visible window
      const { percentOnline: newOnline, percentOffline: newOffline } = calculateOnlineOfflinePercent(chartData, [
        newDomainMs[0],
        newDomainMs[1],
      ]);
      setPercent({ online: newOnline, offline: newOffline });
    };

    // Store refs for external zoom sync
    xScaleRef.current = xScale;
    updateChartRef.current = updateChart;

    // Restore zoom domain if the chart re-rendered due to a resize
    if (currentDomainRef.current) {
      updateChart(currentDomainRef.current);
    }

    // Reset zoom – exposed to React via ref so the React button can call it
    const resetZoom = () => {
      currentDomainRef.current = null;
      updateChart(originalDomainMs);
      // Notify parent to sync other charts
      if (onZoomChange) {
        onZoomChange(null);
      }
    };
    resetZoomRef.current = resetZoom;

    // 11. Zoom: rubber-band brush selection (mirrors zoomType:"x")
    //     Shift + drag to pan (mirrors panKey:"shift")
    let isPanning = false;
    let panStartClientX = 0;
    let panStartDomainMs = [...originalDomainMs];

    const brush = d3
      .brushX()
      .extent([
        [0, 0],
        [width, height],
      ])
      // Exclude shift-key drag (reserved for panning) and non-primary buttons
      .filter(event => !event.shiftKey && !event.button)
      .on("end", function (event) {
        if (!event.selection) return;
        const [x0, x1] = event.selection;
        const tMin0 = xScale.invert(x0).getTime();
        const tMax0 = xScale.invert(x1).getTime();

        // Enforce minimum zoom window (5-second precision)
        const [tMin, tMax] = clampZoomDomain(tMin0, tMax0, originalDomainMs);

        // Clear the brush selection rectangle
        brushG.call(brush.clear);
        updateChart([tMin, tMax]);

        // Notify parent to sync other charts
        if (onZoomChange) {
          onZoomChange({ min: tMin, max: tMax, source: "step" });
        }
      });

    const brushG = g.append("g").attr("class", "brush").call(brush);

    // Attach tooltip events to the brush overlay (covers the full plot area)
    brushG
      .select(".overlay")
      .on("mousemove.tooltip", showTooltip)
      .on("mouseleave.tooltip", () => tooltipDiv.style("display", "none"));

    // Shift+drag panning
    brushG.on("mousedown.pan", function (event) {
      if (!event.shiftKey) return;
      isPanning = true;
      panStartClientX = event.clientX;
      panStartDomainMs = xScale.domain().map(d => d.getTime());
    });

    const handleMouseMovePan = event => {
      if (!isPanning) return;
      const dx = event.clientX - panStartClientX;
      const domainWidth = panStartDomainMs[1] - panStartDomainMs[0];
      const dxTime = domainWidth * (dx / width);

      let newMin = panStartDomainMs[0] - dxTime;
      let newMax = panStartDomainMs[1] - dxTime;

      // Clamp so the panned window never exceeds the original data range
      if (newMin < originalDomainMs[0]) {
        newMin = originalDomainMs[0];
        newMax = newMin + domainWidth;
      }
      if (newMax > originalDomainMs[1]) {
        newMax = originalDomainMs[1];
        newMin = newMax - domainWidth;
      }

      updateChart([newMin, newMax]);

      // Notify parent during pan (throttled by mousemove)
      if (onZoomChange) {
        onZoomChange({ min: newMin, max: newMax, source: "step" });
      }
    };

    const handleMouseUpPan = () => {
      isPanning = false;
    };

    window.addEventListener("mousemove", handleMouseMovePan);
    window.addEventListener("mouseup", handleMouseUpPan);

    // Double-click resets zoom
    brushG.on("dblclick.reset", resetZoom);

    // 12. Cleanup
    return () => {
      window.removeEventListener("mousemove", handleMouseMovePan);
      window.removeEventListener("mouseup", handleMouseUpPan);
      tooltipDiv.remove();
      d3.select(container).selectAll("*").remove();
      resetZoomRef.current = null;
    };
  }, [data, dateRange, isFullscreen, onZoomChange, containerSize, siteTimezone]);

  // ── Sync zoom from external source (other charts) ────────────────────────────
  useEffect(() => {
    if (!resetZoomRef.current) return;

    // Handle reset (zoomExtremes === null)
    if (zoomExtremes === null) {
      resetZoomRef.current();
      return;
    }

    // Ignore updates from this chart
    if (zoomExtremes.source === "step" || !updateChartRef.current) return;

    // Apply zoom from other chart
    updateChartRef.current([zoomExtremes.min, zoomExtremes.max]);
  }, [zoomExtremes]);

  // ── Export handlers via custom hook (matches LineBarComposedChart_new pattern) ────────
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const exportActions = useStepChartExport(wrapperRef, svgContainerRef, chartDataRef, closeMenu, siteTimezone);

  return (
    <div ref={wrapperRef} className="relative w-full rounded-lg shadow-md p-4 overflow-visible chart-wrapper">
      {/* ── Header: title + legend + export button ── */}
      <div className="relative chart-header">
        <div className="chart-title">Connection status</div>

        <div className="absolute top-30 left-0 z-10">
          {!loading && data && data.length > 0 ? (
            <OnlineOfflineLegend percentOnline={percent.online} percentOffline={percent.offline} />
          ) : null}
        </div>

        {/* Export context-menu button */}
        <div className="absolute top-0 right-0">
          <ChartContextMenu
            open={menuOpen}
            onToggle={() => setMenuOpen(prev => !prev)}
            menuRef={menuRef}
            actions={exportActions}
          />
        </div>
      </div>

      {/* ── Chart area ── */}
      <div className="chart-area">
        {!loading && (!data || data.length === 0) ? (
          <NoData />
        ) : loading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center chart-loading-overlay">
            <Loader />
          </div>
        ) : null}

        {/* D3 renders the SVG into this div */}
        <div ref={svgContainerRef} className="w-full h-full mt-5" />
      </div>
    </div>
  );
}

StepGraph.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      ts: PropTypes.number.isRequired,
      s: PropTypes.number.isRequired,
      rsn: PropTypes.string,
    })
  ),
  loading: PropTypes.bool.isRequired,
  dateRange: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.object])),
  onZoomChange: PropTypes.func,
  zoomExtremes: PropTypes.shape({
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    source: PropTypes.string.isRequired,
  }),
  siteTimezone: PropTypes.string,
};

StepGraph.defaultProps = {
  data: [],
  dateRange: undefined,
  onZoomChange: null,
  zoomExtremes: null,
  siteTimezone: "UTC",
};

export default StepGraph;
